import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import type { AssetResponse } from '../../api';
import { publishAssets } from '../../api';
import { getPublishBlockers } from '../../constants/taxonomy';

/**
 * Publishing a selection: the eligibility arithmetic, the call, and the
 * partial-success reporting.
 *
 * Extracted from `SelectionToolbar` because publish now has a second call site.
 * A bulk edit happens in the overlay, and the overlay covers the viewport — the
 * toolbar's Publish button is not merely out of sight there, it is unreachable
 * without closing the edit first. Duplicating the eligibility rule at that
 * second site is how the two would start disagreeing about what "publishable"
 * means; one hook means the tooltip, the disabled state and the request are
 * decided once.
 *
 * The rule itself lives in `constants/taxonomy`, shared with the card's warning
 * badge, so the button and the badge cannot tell different stories about the
 * same row.
 */

/**
 * Publishing requires the format's full metadata set, a source image to have
 * been named (unless the asset is one, or is filmic), and unexpired rights.
 * Knowable from the row, which is why the skip count can be shown before the
 * click rather than discovered in the response.
 */
export function isUnpublishable(asset: AssetResponse, today?: string): boolean {
  return getPublishBlockers(asset, today).length > 0;
}

/**
 * One short phrase naming why these rows cannot publish.
 *
 * Shared blockers only: when every selected row is stuck on the same thing,
 * that is the sentence worth showing. Otherwise it degrades to a generic
 * phrase, because listing six different reasons in a `title` attribute is not
 * something anyone reads — the per-row detail is on the card badges.
 */
function describeBlocker(assets: AssetResponse[]): string {
  const generic = 'incomplete metadata, no source image, or expired rights';
  if (assets.length === 0) return generic;

  const shared = getPublishBlockers(assets[0]).filter((blocker) =>
    assets.every((asset) => getPublishBlockers(asset).includes(blocker)),
  );
  if (shared.length === 0) return generic;

  const named = shared.slice(0, 3).join(', ').toLowerCase();
  return `${named}${shared.length > 3 ? ' and more' : ''}`;
}

export interface AssetPublish {
  /** Fire the publish call for the current selection. */
  publish: () => void;
  /** Same, awaitable, for callers that need to sequence something after it. */
  publishAsync: () => Promise<boolean>;
  /** How many of the selection would actually publish. */
  publishable: number;
  /** How many the backend would skip. */
  unpublishableCount: number;
  /** Tooltip explaining a disabled button or an expected partial skip. */
  title: string | undefined;
  isPending: boolean;
}

interface Options {
  /** Run after every asset in the call published cleanly. */
  onPublished?: () => void;
  /**
   * Run with the ids the backend refused, when some published and some did not.
   *
   * The caller narrows its selection to these. Without it, a partial publish
   * drops the successful rows out of the list, the selection prunes itself to
   * the remainder and the bulk editor closes on its way past two members —
   * taking the skipped rows off screen at the exact moment they need fixing.
   */
  onPartiallyPublished?: (skippedIds: string[]) => void;
}

export function useAssetPublish(
  selectedAssets: AssetResponse[],
  { onPublished, onPartiallyPublished }: Options = {},
): AssetPublish {
  const queryClient = useQueryClient();

  // Wrapped rather than passed by reference: `filter` hands the index as the
  // second argument, which would land in `today`.
  const unpublishableCount = selectedAssets.filter((asset) => isUnpublishable(asset)).length;
  const publishable = selectedAssets.length - unpublishableCount;

  const mutation = useMutation({
    mutationFn: () => publishAssets({ asset_ids: selectedAssets.map((asset) => asset.id) }),
    onSuccess: (result) => {
      // Partial-success, like every bulk endpoint here: the skipped list has to
      // be surfaced or a user walks away believing all of them published.
      if (result.skipped.length > 0) {
        toast.error(
          `Published ${result.updated.length}, skipped ${result.skipped.length}: ${result.skipped[0].reason}`,
        );
        // Before the invalidation below, so the caller's selection is already
        // narrowed by the time the refetched list prunes it.
        onPartiallyPublished?.(result.skipped.map((entry) => entry.asset_id));
      } else {
        toast.success(
          `Published ${result.updated.length} asset${result.updated.length === 1 ? '' : 's'}`,
        );
        // Only on a clean run. Closing the overlay on a partial skip would hide
        // the very rows that still need fixing.
        onPublished?.();
      }
      // Published rows leave the Staged tab, and `useAssetSelection` prunes
      // ids that are no longer in the list, so the selection empties itself.
      void queryClient.invalidateQueries({ queryKey: ['asset-library', 'assets'] });
      // The source image picker lists published heroes only, so a hero just
      // published must appear there before its derivatives can be linked.
      void queryClient.invalidateQueries({ queryKey: ['asset-library', 'key-visuals'] });
    },
    onError: () => toast.error('Could not publish'),
  });

  return {
    publish: () => mutation.mutate(),
    publishAsync: async () => {
      try {
        const result = await mutation.mutateAsync();
        return result.skipped.length === 0;
      } catch {
        return false;
      }
    },
    publishable,
    unpublishableCount,
    // The skip count lives in the tooltip rather than the label: the count is
    // already on "N selected", and a button that grew a "· 3 skipped" suffix
    // was carrying the same arithmetic twice.
    title:
      publishable === 0
        ? // Naming the blocker, now that the predicate actually knows it. "Add
          // expiry rights" is actionable in a way that "incomplete metadata"
          // never was — the latter sends people field-hunting through a rail of
          // twenty inputs.
          `Cannot publish: ${describeBlocker(selectedAssets)}`
        : unpublishableCount > 0
          ? `${unpublishableCount} of ${selectedAssets.length} will be skipped: ${describeBlocker(
              selectedAssets.filter((asset) => isUnpublishable(asset)),
            )}`
          : undefined,
    isPending: mutation.isPending,
  };
}
